import React, { useEffect, useRef } from "react";

import { getMarkerSVG } from "@/lib/map-utils";
import { cn } from "@/lib/utils";

import type { PlaceMarker } from "@/model/place";
declare global {
  interface Window {
    google: typeof google;
  }
}

type MapviewPropsType = {
  searchResult: PlaceMarker[];
  /**
   * Overrides the map canvas's default fixed height — pass a flex/height class
   * to let it fill a container instead.
   */
  className?: string;
  /**
   * Caps how far `fitBounds` may zoom in. Worth setting when there is a single
   * marker: a zero-area bounds otherwise zooms to street level, which shows a
   * rooftop and no context.
   */
  maxZoom?: number;
};

const MapView: React.FunctionComponent<MapviewPropsType> = ({
  searchResult,
  className,
  maxZoom,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  // console.log("## searchResult", searchResult);
  useEffect(() => {
    if (!searchResult || searchResult.length === 0) return;

    // 🔄 Reset the map container
    if (mapRef.current) {
      mapRef.current.innerHTML = "";
    }

    const initMap = () => {
      class TooltipOverlay extends window.google.maps.OverlayView {
        div: HTMLDivElement | null = null;
        constructor(
          private position: google.maps.LatLngLiteral,
          private text: string,
        ) {
          super();
        }
        onAdd() {
          this.div = document.createElement("div");
          this.div.className =
            "bg-white text-black text-xs font-medium px-2 py-1 rounded shadow border border-gray-300";
          this.div.style.position = "absolute";
          this.div.style.whiteSpace = "nowrap";
          this.div.style.pointerEvents = "none";
          this.div.innerText = this.text;
          const panes = this.getPanes();
          panes?.floatPane.appendChild(this.div);
        }
        draw() {
          if (!this.div) return;
          const projection = this.getProjection();
          const pos = projection.fromLatLngToDivPixel(
            new window.google.maps.LatLng(this.position),
          );
          if (pos && this.div) {
            this.div.style.left = `${pos.x - this.div.offsetWidth / 2}px`;
            this.div.style.top = `${pos.y - this.div.offsetHeight - 10}px`;
          }
        }
        onRemove() {
          this.div?.parentNode?.removeChild(this.div);
          this.div = null;
        }
      }

      const bounds = new window.google.maps.LatLngBounds();
      const center = {
        lat: searchResult[0]?.lat ?? searchResult[0]?.lat,
        lng: searchResult[0]?.lng ?? searchResult[0]?.lng,
      };

      const map = new window.google.maps.Map(mapRef.current!, {
        center,
        zoom: 12,
        ...(maxZoom === undefined ? {} : { maxZoom }),
      });

      searchResult.forEach((loc) => {
        const position = { lat: loc.lat, lng: loc.lng };
        const svg = getMarkerSVG(loc.pinSymbol as "Pin" | "Star", loc.color);
        const encodedSvg = svg
          ? `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
          : undefined;

        const marker = new window.google.maps.Marker({
          position,
          map,
          title: loc.name,
          ...(encodedSvg && {
            icon: {
              url: encodedSvg,
              scaledSize: new window.google.maps.Size(20, 28),
              anchor: new window.google.maps.Point(10, 28),
            },
          }),
        });

        let tooltip: TooltipOverlay | null = null;

        marker.addListener("mouseover", () => {
          tooltip = new TooltipOverlay(position, loc.name);
          tooltip.setMap(map);
        });

        marker.addListener("mouseout", () => {
          tooltip?.setMap(null);
          tooltip = null;
        });

        bounds.extend(position);
      });

      map.fitBounds(bounds);
    };

    if (!window.google?.maps) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBH0ihBz-bpjiqZuMtQStnz9Gekf3FXe1U`;
      script.async = true;
      script.onload = initMap;
      document.head.appendChild(script);
    } else {
      initMap();
    }
  }, [searchResult, maxZoom]); // re-run when props change

  return (
    <>
      <div
        ref={mapRef}
        className={cn("mb-4 h-48 w-full rounded border", className)}
      />
      <div className="space-y-3 p-2">
        {searchResult.map((loc, index) => (
          <div
            key={index}
            className="rounded border bg-gray-50 p-3 shadow-sm transition hover:bg-gray-100"
          >
            <h3 className="text-sm font-medium">{loc.name}</h3>
            <p className="text-xs text-gray-600">{loc.address}</p>
            <p className="mt-1 text-xs text-gray-500">
              {loc?.distance && loc?.distance < 1
                ? Math.round(loc?.distance * 10) / 10
                : Math.round(loc?.distance)}{" "}
              miles (approx)
            </p>
          </div>
        ))}
      </div>
    </>
  );
};

export default MapView;
